import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";

export default function Privacy() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Legal</div>
        <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="text-stone-500 text-sm">Last updated Feb 27, 2026</p>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 my-6 text-sm text-amber-900">
          <b>Template notice:</b> This Privacy Policy is a starting draft. Before accepting paid
          customers, have it reviewed by an attorney for GDPR, CCPA, and HIPAA applicability.
        </div>

        <Section title="1. Information We Collect">
          <ul>
            <li><b>Account data</b>: name, email, hashed password, account creation timestamp, IP at account creation (for ToS acceptance record).</li>
            <li><b>User content</b>: formulations, ingredient entries, AI assistant conversations you create in the Service.</li>
            <li><b>Usage data</b>: pages visited, features used, error logs.</li>
            <li><b>Payment data</b>: processed by our payment provider (Stripe). We never store full card numbers.</li>
          </ul>
        </Section>

        <Section title="2. How We Use It">
          To operate the Service, authenticate you, respond to AI queries, provide support,
          send transactional emails (account, billing), and comply with legal obligations.
          We do <b>not</b> sell your data.
        </Section>

        <Section title="3. Third-Party Processors">
          <p className="mb-2">
            Alaine's Bench relies on a small set of trusted infrastructure
            providers (each is a separate data controller for its own scope and
            publishes its own privacy/DPA terms):
          </p>
          <ul>
            <li><b>MongoDB Atlas</b> (US region) — hosts your account, formulations, ingredients, and lot/inventory records. See mongodb.com/legal/privacy-policy.</li>
            <li><b>Stripe, Inc.</b> — processed payments and stores billing data for historical transactions from when public plans were offered. We never see or store full card numbers. See stripe.com/privacy.</li>
            <li><b>Anthropic, PBC</b> — provides the Claude model that powers the AI compliance assistant. Prompts you submit to the assistant are sent to Anthropic for inference. See anthropic.com/legal/privacy.</li>
            <li><b>Replit, Inc.</b> — provides the application hosting platform and the AI gateway through which Claude requests are routed. See replit.com/privacy.</li>
          </ul>
          <p className="mt-2">
            We do <b>not</b> share your data with advertising networks, data
            brokers, or analytics vendors.
          </p>
        </Section>

        <Section title="4. Cookies & Local Storage">
          <p className="mb-2">
            Alaine's Bench uses <b>essential storage only</b>. We do not load
            third-party analytics, behavioral-advertising pixels, or
            fingerprinting scripts.
          </p>
          <ul>
            <li><b>Auth token</b> (HTTP-only cookie + optional localStorage fallback) — keeps you signed in. Cleared on logout.</li>
            <li><b>UI preferences</b> (localStorage) — e.g. the cookie-notice acknowledgement, current product-type tab, AI conversation drafts.</li>
            <li><b>Stripe</b> set its own essential cookies on any checkout pages it served for historical transactions; those are governed by Stripe's privacy policy.</li>
          </ul>
          <p className="mt-2">
            You can clear all storage at any time from your browser settings;
            doing so will sign you out.
          </p>
        </Section>

        <Section title="5. Your Rights (CCPA / GDPR)">
          You may request access to, correction of, or deletion of your personal data at any time
          by emailing us. We will respond within 30 days. EU and California residents have additional
          rights under GDPR and the CCPA, including the right to opt out of the "sale" of personal
          information (we do not sell personal information).
        </Section>

        <Section title="6. Data Retention">
          We retain account data while your account is active and for 90 days after deletion request.
          Backups are rotated every 90 days.
        </Section>

        <Section title="7. Children">
          The Service is not intended for individuals under 16. We do not knowingly collect data
          from minors.
        </Section>

        <Section title="8. Contact">
          Privacy inquiries: <Link to="/contact" className="text-emerald-800 underline underline-offset-2">contact us</Link>.
          Operating entity: Shirleys Daughter, LLC.
        </Section>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold text-stone-900">{title}</h2>
      <div className="text-stone-700 text-sm leading-relaxed mt-2">{children}</div>
    </div>
  );
}
